// Suggestion chips that follow where the user is (one data module, easy to
// edit). A chip's `prompt` goes into the composer for the user to adjust and
// send, like the saved-question chips.
//   PENYEDIA_STAGE_CHIPS   a Penyedia paket project, by its stage; a chip with
//                          `hideWhenDone` disappears once that checklist step
//                          (its Kode Proses) is ticked
//   OWNER_PHASE_CHIPS      a Non-Penyedia guest, by procurement phase
//   pbjStageChips          a Non-Penyedia PBJ project, by its stage, naming the paket
//   GUEST_*_WORKFLOWS      the guided guest flow's workflow choices
import type { PaketStage, PbjStage } from "../api";

/** What the paket intake chips put into the composer; sent alone (nothing pasted) they are refused. */
export const INTAKE_PREFILL = {
  paste: "Ini teks pengumuman paket dari LPSE:\n",
  tell: "Paket yang ingin saya ikuti: ",
} as const;

/** True when the composer holds only an intake chip's opening line, with nothing after it. */
export function isBareIntakePrefill(text: string): boolean {
  const t = text.trim();
  return Object.values(INTAKE_PREFILL).some((p) => p.trim() === t);
}

export interface SuggestionChip {
  label: string;
  prompt: string;
  /** A checklist item key, or the keys one activity has across process groups: the chip hides once one is done. */
  hideWhenDone?: string | readonly string[];
}

export const PENYEDIA_STAGE_CHIPS: Record<PaketStage, SuggestionChip[]> = {
  persiapan_pemilihan: [
    { label: "Ringkas dokumen pemilihan", prompt: "Ringkas dokumen pemilihan paket ini: lingkup, jadwal, syarat kualifikasi, dan cara evaluasi.", hideWhenDone: "02-04" },
    { label: "Cek kualifikasi usaha saya", prompt: "Cek kualifikasi usaha saya untuk paket ini (KBLI, SBU, pengalaman, personel). Data usaha saya: " },
    { label: "Layak ikut paket ini?", prompt: "Apakah paket ini layak saya ikuti? Bantu saya menimbang peluang, risiko, dan kesiapan.", hideWhenDone: "02-08" },
  ],
  pelaksanaan_pemilihan: [
    { label: "Susun dokumen teknis", prompt: "Susun dokumen teknis untuk paket ini: metodologi, jadwal pelaksanaan, dan daftar personel.", hideWhenDone: ["03-07", "06-04", "07-05"] },
    { label: "Hitung harga penawaran", prompt: "Hitung harga penawaran (RAB) untuk paket ini. Rincian pekerjaan: [uraian, volume, satuan, harga satuan].", hideWhenDone: ["03-08", "06-06", "07-06"] },
    { label: "Daftar dokumen yang wajib diunggah", prompt: "Buat daftar dokumen penawaran yang wajib diunggah ke SPSE untuk paket ini.", hideWhenDone: ["03-10", "04-06", "07-07"] },
    { label: "Cara membaca hasil evaluasi", prompt: "Bagaimana cara membaca berita acara hasil evaluasi dan pengumuman pemenang paket ini?", hideWhenDone: "03-12" },
    { label: "Susun sanggah", prompt: "Susun surat sanggah untuk paket ini. Alasan keberatan: [isi alasan].", hideWhenDone: ["03-13", "18-03", "04-09", "07-08"] },
  ],
  pelaksanaan_kontrak: [
    { label: "Periksa rancangan kontrak", prompt: "Apa saja yang perlu saya periksa di rancangan kontrak paket ini sebelum tanda tangan?", hideWhenDone: "13-04" },
    { label: "Susun laporan progres", prompt: "Susun laporan progres pekerjaan paket ini. Progres saat ini [persen], rencana [persen].", hideWhenDone: "14-06" },
    { label: "Kapan perlu adendum?", prompt: "Kapan saya perlu mengajukan adendum kontrak, dan dokumen apa yang disiapkan?" },
    { label: "Hitung denda keterlambatan", prompt: "Hitung denda keterlambatan: nilai kontrak [nilai], terlambat [jumlah] hari. Bagaimana ketentuannya?" },
  ],
  serah_terima: [
    { label: "Siapkan BAST", prompt: "Apa saja yang perlu disiapkan untuk BAST paket ini?", hideWhenDone: "17-06" },
    { label: "Susun tagihan pembayaran", prompt: "Susun surat tagihan pembayaran untuk paket ini.", hideWhenDone: "17-08" },
    { label: "Pembayaran terlambat, apa langkahnya?", prompt: "Pembayaran paket ini terlambat. Apa langkah yang bisa saya tempuh?" },
  ],
  selesai: [{ label: "Pelajaran dari paket ini", prompt: "Bantu saya mencatat pelajaran dari paket ini untuk paket berikutnya." }],
  ditutup: [{ label: "Pelajaran dari paket ini", prompt: "Paket ini tidak terpilih. Bantu saya mencatat pelajaran untuk paket berikutnya." }],
};

/** The checklist keys that hide a chip. */
function hidesFor(c: SuggestionChip): readonly string[] {
  return c.hideWhenDone === undefined ? [] : typeof c.hideWhenDone === "string" ? [c.hideWhenDone] : c.hideWhenDone;
}

/** The chips of a stage, without those whose checklist item is already done. */
export function chipsForStage(stage: PaketStage | null | undefined, doneKeys: readonly string[] = []): SuggestionChip[] {
  const all = PENYEDIA_STAGE_CHIPS[stage ?? "persiapan_pemilihan"] ?? [];
  return all.filter((c) => !hidesFor(c).some((k) => doneKeys.includes(k)));
}

// ------------------------------------------- Non-Penyedia PBJ project chat

/** A government PBJ project's chips, by its stage, naming the paket (never a generic laptop example). */
export function pbjStageChips(stage: PbjStage | null | undefined, paketName: string): SuggestionChip[] {
  const p = paketName.trim() || "ini";
  const chips: Record<PbjStage, SuggestionChip[]> = {
    identifikasi_kebutuhan: [
      { label: "Susun identifikasi kebutuhan", prompt: `Buatkan identifikasi kebutuhan untuk paket ${p}.` },
      { label: "Susun spesifikasi / KAK", prompt: `Buatkan spesifikasi teknis atau KAK sesuai kebutuhan paket ${p}.` },
      { label: "Cek ketersediaan pasar", prompt: `Bantu saya memeriksa ketersediaan barang/jasa dan penyedia di pasar untuk paket ${p}.` },
      { label: "Susun RAB", prompt: `Buatkan RAB paket ${p} sesuai spesifikasi teknis/KAK sebagai dasar pengusulan anggaran.` },
    ],
    perencanaan: [
      { label: "Tentukan cara pengadaan", prompt: `Bantu saya menentukan cara pengadaan dan metode pemilihan untuk paket ${p}.` },
      { label: "Data RUP yang disiapkan", prompt: `Apa saja data RUP yang perlu saya siapkan untuk paket ${p}?` },
    ],
    persiapan_pengadaan: [
      { label: "Susun KAK / spesifikasi", prompt: `Buatkan KAK untuk paket ${p}.` },
      { label: "Susun HPS", prompt: `Buatkan HPS untuk paket ${p}. Rincian item: [uraian, volume, satuan, harga satuan].` },
      { label: "Susun rancangan kontrak", prompt: `Buatkan rancangan kontrak untuk paket ${p}.` },
    ],
    persiapan_pemilihan: [
      { label: "Susun dokumen pemilihan", prompt: `Buatkan dokumen pemilihan untuk paket ${p}.` },
      { label: "Nota dinas pengajuan", prompt: `Buatkan nota dinas pengajuan pemilihan untuk paket ${p}.` },
      { label: "Reviu dokumen persiapan", prompt: `Apa yang perlu direviu dari dokumen persiapan paket ${p} sebelum pemilihan?` },
    ],
    pelaksanaan_pemilihan: [
      { label: "Evaluasi penawaran", prompt: `Bagaimana langkah mengevaluasi penawaran untuk paket ${p}?` },
      { label: "Berita acara hasil pemilihan", prompt: `Buatkan berita acara hasil pemilihan untuk paket ${p}.` },
    ],
    pelaksanaan_kontrak: [
      { label: "Laporan kemajuan", prompt: `Buatkan laporan kemajuan pelaksanaan paket ${p}.` },
      { label: "Perubahan kontrak", prompt: `Kapan kontrak paket ${p} perlu diubah, dan dokumen apa yang disiapkan?` },
    ],
    serah_terima: [
      { label: "Berita acara serah terima", prompt: `Buatkan berita acara serah terima untuk paket ${p}.` },
      { label: "Pemeriksaan hasil pekerjaan", prompt: `Apa yang diperiksa sebelum serah terima paket ${p}?` },
    ],
  };
  return chips[stage ?? "identifikasi_kebutuhan"] ?? chips.identifikasi_kebutuhan;
}

/** The drafting example inside a plain (folder) project: the project's own name, the role's kind of document. */
export function projectDraftExample(role: string | null | undefined, projectName: string): string {
  const name = projectName.trim();
  if (role === "penyedia") return name ? `Buatkan surat penawaran untuk ${name}` : "Buatkan surat penawaran untuk paket ini";
  return name ? `Buatkan KAK untuk ${name}` : "Buatkan KAK untuk paket ini";
}

// ---------------------------------------------------- guided guest flow

export type GuestPenyediaWorkflow = "cari_paket" | "tender" | "harga" | "kontrak" | "serah_terima";
export type GuestOwnerWorkflow = "perencanaan" | "persiapan_pengadaan" | "pemilihan" | "kontrak_owner";

export const GUEST_PENYEDIA_WORKFLOWS: { id: GuestPenyediaWorkflow; label: string; stage: PaketStage }[] = [
  { id: "cari_paket", label: "Mencari & menilai paket", stage: "persiapan_pemilihan" },
  { id: "tender", label: "Persiapan tender & dokumen penawaran", stage: "pelaksanaan_pemilihan" },
  { id: "harga", label: "Hitung harga penawaran", stage: "pelaksanaan_pemilihan" },
  { id: "kontrak", label: "Pelaksanaan kontrak", stage: "pelaksanaan_kontrak" },
  { id: "serah_terima", label: "Serah terima & pembayaran", stage: "serah_terima" },
];

export const GUEST_OWNER_WORKFLOWS: { id: GuestOwnerWorkflow; label: string }[] = [
  { id: "perencanaan", label: "Perencanaan (RUP)" },
  { id: "persiapan_pengadaan", label: "Persiapan pengadaan (KAK, HPS, spesifikasi)" },
  { id: "pemilihan", label: "Persiapan & pelaksanaan pemilihan" },
  { id: "kontrak_owner", label: "Kontrak & serah terima" },
];

export const OWNER_PHASE_CHIPS: Record<GuestOwnerWorkflow, SuggestionChip[]> = {
  perencanaan: [
    { label: "Cara menyusun RUP", prompt: "Bagaimana cara menyusun dan mengumumkan Rencana Umum Pengadaan (RUP)?" },
    { label: "Pilih cara pengadaan", prompt: "Bantu saya memilih cara pengadaan: melalui penyedia atau swakelola, untuk kebutuhan berikut: " },
    { label: "Jadwal perencanaan", prompt: "Susun jadwal perencanaan pengadaan untuk tahun anggaran berikutnya." },
  ],
  persiapan_pengadaan: [
    { label: "Susun KAK", prompt: "Susun kerangka acuan kerja (KAK) untuk pekerjaan berikut: " },
    { label: "Hitung HPS", prompt: "Hitung HPS dengan rincian: [uraian, volume, satuan, harga satuan], PPN 11%." },
    { label: "Spesifikasi teknis", prompt: "Bantu saya menyusun spesifikasi teknis yang tidak mengarah ke merek tertentu untuk: " },
  ],
  pemilihan: [
    { label: "Metode pemilihan yang tepat", prompt: "Metode pemilihan apa yang tepat untuk paket dengan nilai dan jenis berikut: " },
    { label: "Jadwal tender", prompt: "Susun jadwal tender dari pengumuman sampai penetapan pemenang." },
    { label: "Menjawab sanggah", prompt: "Bagaimana Pokja menjawab sanggah peserta dengan benar?" },
  ],
  kontrak_owner: [
    { label: "Isi rancangan kontrak", prompt: "Apa saja isi pokok rancangan kontrak pengadaan?" },
    { label: "Kapan adendum boleh dibuat?", prompt: "Kapan adendum kontrak boleh dibuat, dan apa batasannya?" },
    { label: "Pemeriksaan dan BAST", prompt: "Bagaimana prosedur pemeriksaan pekerjaan dan BAST?" },
  ],
};

/** The chips for a guest's workflow choice (Penyedia: the stage chips; Non-Penyedia: the phase chips). */
export function guestChips(role: "penyedia" | "non_penyedia" | null, workflow: string | null): SuggestionChip[] {
  if (role === "penyedia") {
    const w = GUEST_PENYEDIA_WORKFLOWS.find((x) => x.id === workflow);
    if (!w) return [];
    const chips = PENYEDIA_STAGE_CHIPS[w.stage];
    // Hitung harga penawaran leads for that choice.
    return w.id === "harga" ? [...chips].sort((a, b) => (hidesFor(a).includes("03-08") ? -1 : hidesFor(b).includes("03-08") ? 1 : 0)) : chips;
  }
  if (role === "non_penyedia") return OWNER_PHASE_CHIPS[workflow as GuestOwnerWorkflow] ?? [];
  return [];
}
